import {
  getFindAllVenueRentalConfigurationsQueryKey,
  useCreateVenueRentalConfiguration,
  VenueConfigurationUnionType,
  VenueConfigurationVersionUnionType,
} from '@api';
import { useInvalidateQuery, useNotification } from '@common';
import { AxiosError } from 'axios';
import { RentalConfigurationFormValuesType } from '../../components';

const TRANS_PATH_NOTIFICATION = 'NOTIFICATIONS.ADD_RENTAL_CONFIGURATION';

type UseAddRentalConfigurationArgsType = {
  venueId: string;
  version: string;
};

type UseAddRentalConfigurationReturnType = {
  addRentalConfiguration: (
    data: RentalConfigurationFormValuesType
  ) => Promise<void>;
};

export const useAddRentalConfiguration = ({
  venueId,
  version,
}: UseAddRentalConfigurationArgsType): UseAddRentalConfigurationReturnType => {
  const { showSuccess, showError } = useNotification();
  const { mutateAsync: mutateAdd } = useCreateVenueRentalConfiguration();
  const { invalidateQuery: invalidateConfigurationsQueries } =
    useInvalidateQuery(
      getFindAllVenueRentalConfigurationsQueryKey(venueId, version).filter(
        Boolean
      )
    );

  const onSuccess = async () => {
    invalidateConfigurationsQueries();
    showSuccess({
      title: `${TRANS_PATH_NOTIFICATION}.SUCCESS.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.SUCCESS.MESSAGE`,
    });
  };

  const onError = (error: AxiosError) => {
    showError({
      title: `${TRANS_PATH_NOTIFICATION}.ERROR.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.ERROR.MESSAGE`,
      error: error,
    });
  };

  const addRentalConfiguration = async (
    data: RentalConfigurationFormValuesType
  ) => {
    await mutateAdd(
      {
        venueId,
        data: {
          validFrom: data.validFrom,
          validUntil: data.validUntil ?? undefined,
          configuration: data.configuration as VenueConfigurationUnionType,
          version: data.version as VenueConfigurationVersionUnionType,
          capacity: data.capacity ?? undefined,
          rentalPercentageNet: data.rentalPercentageNet ?? undefined,
          rentalPercentageGross: data.rentalPercentageGross ?? undefined,
          rentalFlatFee: data.rentalFlatFee ?? undefined,
          minimumRental: data.minimumRental ?? undefined,
          maximumRental: data.maximumRental ?? undefined,
          fixedVenueSideCosts: data.fixedVenueSideCosts ?? undefined,
          rentalSharePerTicket: data.rentalSharePerTicket ?? undefined,
          rentalShareBasis: data.rentalShareBasis ?? undefined,
          notes: data.notes ?? undefined,
        },
      },
      {
        onSuccess,
        onError,
      }
    );
  };

  return { addRentalConfiguration };
};
